import { useGraphDocumentData } from "@features/builder/GraphDocumentProvider/hooks/useGraphDocumentData"
import { GraphDetails } from "@features/builder/types"
import { Autocomplete, TextField } from "@mui/material"
import { useGetScenarioSourceMnemonicsQuery } from "@store/services/graphsService"
import { getNormalisedGraphKey } from "@utils/graph"
import { ReactNode, useMemo } from "react"
import { Control, Controller, FieldValues, Path } from "react-hook-form"

const EMPTY_HISTORIC_MNEMONIC_OPTION =
  "No historic data mnemonics found in the attached scenario file."

type HistoricMnemonicFieldProps<TFieldValues extends FieldValues> = {
  control: Control<TFieldValues>
  name: Path<TFieldValues>
  label?: string
  children?: ReactNode
  disabled?: boolean
}

export const HistoricMnemonicField = <TFieldValues extends FieldValues>(
  props: HistoricMnemonicFieldProps<TFieldValues>,
) => {
  const graphDocumentData = useGraphDocumentData()
  const graphData: GraphDetails | null = JSON.parse(graphDocumentData.data)
  const scenarioFilePath = graphData?.ScenarioSource
  const graphKey = graphData ? getNormalisedGraphKey(graphData) : ""

  const getMnemonicsQuery = useGetScenarioSourceMnemonicsQuery(
    { graphKey, type: "historic" },
    {
      skip: !scenarioFilePath,
    },
  )

  const mnemonics = useMemo(() => getMnemonicsQuery.data ?? [], [getMnemonicsQuery.data])

  const historicOptions = useMemo(
    () =>
      scenarioFilePath && mnemonics.length === 0
        ? [EMPTY_HISTORIC_MNEMONIC_OPTION]
        : mnemonics.map(({ mnem }) => mnem),
    [scenarioFilePath, mnemonics],
  )

  return (
    <Controller
      control={props.control}
      name={props.name}
      render={({ field, fieldState }) => (
        <Autocomplete
          freeSolo
          options={historicOptions}
          getOptionDisabled={(option) => option === EMPTY_HISTORIC_MNEMONIC_OPTION}
          size="small"
          fullWidth
          disabled={props.disabled || field.disabled}
          slotProps={{
            clearIndicator: {
              // @ts-expect-error  this is valid attribute, wrong MUI typing
              "data-testid": "clear-historic-mnemonic-icon",
            },
          }}
          renderInput={(params) => (
            <>
              <TextField
                label={props.label}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                name={field.name}
                {...params}
                onChange={!scenarioFilePath ? (e) => field.onChange(e.target.value) : undefined}
                InputLabelProps={{ shrink: true, ...params.InputLabelProps }}
              />
              {props.children}
            </>
          )}
          componentsProps={{ popper: { style: { width: "fit-content" } } }}
          {...field}
          onChange={(_event, value) => field.onChange(value ?? "")}
          onInputChange={(_event, value, reason) => {
            if (reason === "input" || reason === "clear") {
              field.onChange(value)
            }
          }}
        />
      )}
    />
  )
}

export default HistoricMnemonicField
